<template>
    <div id="login-modal-wrapper">
        <Modal
            v-bind="modalDimensions"
			v-if="isOpen"
            :slotName="slotName"
            :cardStyles="modalCardStyles"
            :cardHeaderStyles="modalHeaderStyles"
            :isOpen="isOpen"
            @toggleModal="exitModal"
		>
            <template v-slot:title>
                <p :class="modalTitleStyles">{{ modalTitle }}</p>
            </template>

            <!-- Log in -->
            <template v-slot:logIn>
                <div class="error-banner" v-if="error && errorType == null">
                    <p class="error-text">This account doesn't exist. Sign up for it?</p>
                </div>
                <div class="form">
                    <div class="field">
                        <p class="form-label">Email address</p>
                        <input
                            id="email"
                            :class="error ? 'error' : ''"
                            type="text"
                            v-model="email"
                            placeholder="Enter email address"
                            @focus="inputFocus"
                            @blur="emailBlur"
                        />
                        <p class="error-text error-label" v-if="error && errorType == 'email'">
                            That doesn't look like an email address
                        </p>
                        <p class="error-text error-label" v-if="error && errorType == 'uw-email'">
                            That doesn't look like a UW school email address
                        </p>
                    </div>
                    <div class="field">
                        <p class="form-label">Password</p>
                        <input
                            id="password"
                            :class="error && errorType == null ? 'error' : ''"
                            type="password"
                            placeholder="Enter your password"
                            v-model="password"
                            @keyup.enter="submitLogin"
                            @focus="inputFocus"
                            @blur="blur"
                        />
                    </div>
					<p class="error-text error-label" v-if="error && errorType == 'contact-classy'">
						Looks like there is an issue with your account! Contact our support team for more information
					</p>
					<p class="error-text error-label" v-if="error && errorType == 'bad-login'">
						Incorrect username and password combination
					</p>
                    <button class="submit" @click="submitLogin">
                        Log in
                    </button>
                </div>
                <div class="card-footer row-wrap">
                    <p class="minor-text" @click="goTo('logIn', 'reset')">Forgot your password?</p>
                    <p class="minor-text" @click="goTo('logIn', 'signUpOne')">Sign up instead</p>
                </div>
            </template>

            <!-- Reset password -->
            <template v-slot:reset>
                <p class="minor-text subtitle">
                    We gotcha, enter your email below and we'll send you a link to reset your password
                </p>
                <div class="form">
                    <div class="field">
                        <p class="form-label">Email address</p>
                        <input
                            id="email"
                            :class="error ? 'error' : ''"
                            type="text"
                            v-model="email"
                            placeholder="Enter email address"
							@keyup.enter="submitReset"
                            @focus="inputFocus"
                            @blur="emailBlur"
                        />
                        <p class="error-text error-label" v-if="error && errorType == 'email'">
                            That doesn't look like an email address
                        </p>
                        <p class="error-text error-label" v-if="error && errorType == 'uw-email'">
                            That doesn't look like a UW school email address
                        </p>
                        <p class="error-text error-label" v-if="error && errorType == 'account'">
                            An account is already registered with this email address
                        </p>
						<p class="error-text error-label" v-if="error && errorType == 'contact-classy'">
							Looks like there is an issue with your account! Contact our support team for more information
						</p>
                    </div>
                    <button class="submit" @click="submitReset">
                        Send email
                    </button>
                </div>
            </template>

            <!-- Check email -->
            <template v-slot:checkEmail>
                <p class="minor-text subtitle">
                    Click on the link we sent to your email to sign in and reset your password.
                </p>
                <div class="form">
                    <button class="submit" @click="goTo('checkEmail', 'logIn')">
                        Go to sign in
                    </button>
                </div>
            </template>

            <!-- Sign up page 1 -->
            <template v-slot:signUpOne>
                <div class="form">
                    <div class="field">
                        <p class="form-label">Email address</p>
                        <input
                            id="email"
                            :class="error ? 'error' : ''"
                            type="text"
                            v-model="email"
                            placeholder="Enter email address"
                            @focus="inputFocus"
                            @blur="emailBlur"
                        />
                        <p class="error-text error-label" v-if="error && errorType == 'email'">
                            That doesn't look like an email address
                        </p>
                        <p class="error-text error-label" v-else-if="error && errorType == 'uw-email'">
                            That doesn't look like a UW school email address
                        </p>
                    </div>
                    <div class="field">
                        <p class="form-label">Password</p>
                        <input
                            id="password"
                            :class="error && errorType == null ? 'error' : ''"
                            type="password"
                            placeholder="Enter your password"
                            v-model="password"
                            @keyup.enter="signUpNextPage"
                            @focus="inputFocus"
                            @blur="blur"
                        />
                    </div>
                    <button class="submit" @click="signUpNextPage">
                        Next page
                    </button>
                </div>
                <div class="card-footer row-wrap">
                    <p class="minor-text" @click="goTo('signUpOne', 'logIn')">Log in instead</p>
                </div>
            </template>

            <!-- Sign up page 2 -->
            <template v-slot:signUpTwo>
                <div class="form">
                    <div class="field">
                        <p class="form-label">Your name</p>
                        <input
                            type="text"
                            v-model="signUpData.name"
                            placeholder="Enter what you want people to know you as"
                            @focus="inputFocus"
                            @blur="blur"
                        />
                    </div>
                    <div class="field">
                        <p class="form-label">Class standing</p>
                        <div class="label-select">
                            <span
                                class="label-select-item"
                                v-for="(label, idx) in classStandings"
                                :key="`label-${idx}`"
                                :class="{ active: signUpData.class == idx }"
                                @click="onSelectClass(idx)"
                            >
                                {{ label }}
                            </span>
                        </div>
                    </div>
                    <div class="field">
                        <p class="form-label">Major</p>
                        <multiselect
                            v-model="signUpData.major"
                            :options="majorOptions"
                            :multiple="true"
                            :close-on-select="false"
                            :clear-on-select="true"
                            :hide-selected="true"
                            :preserve-search="true"
                            :showNoResults="false"
                            :showLabels="false"
                            @input="onSelectMajor"
                            placeholder="Enter your major"
                            ref="multiselect"
                        >
                        </multiselect>
                    </div>
                    <button
                        class="submit"
                        :class="{ disabled: !signUpCompleted }"
                        :disabled="!signUpCompleted == true"
                        @click="submitSignUp"
                    >
                        Sign up
                    </button>
                </div>
                <div class="card-footer row-wrap">
                    <p class="minor-text" @click="goTo('signUpTwo', 'logIn')">Log in instead</p>
                </div>
            </template>

            <!-- Verify email -->
            <template v-slot:verifyEmail>
                <section id="verify-email">
                    <p class="minor-text subtitle">
                        Click on the link we sent you to complete your profile. If you don’t see it, check your spam
                        folder.
                    </p>
                    <div class="form">
                        <button class="submit" @click="exitModal">
                            okay
                        </button>
                    </div>
                    <div class="card-footer">
                        <p class="soft-text">
                            Still can’t find it?
                            <span class="footer-link minor-text" @click="resendEmail">resend the email</span>
                        </p>
                    </div>
                </section>
            </template>
        </Modal>
    </div>
</template>

<script>
// components
import Modal from "@/components/modals/Modal";
import Multiselect from "vue-multiselect";

// libraries
import { firestore, auth } from "@/firebase.js";

// mixins
import utilMixin from "@/mixins/utilMixin";

// data
import profileJSON from "@/data/profile.json";

const initialState = (display) => {
    return {
        email: null,
        password: null,
        signUpData: {
            name: null,
            class: null,
            major: "",
        },

        show: {
            signUpOne: display == "signUp",
            signUpTwo: false,
            logIn: display == "login",
            reset: display == "reset",
            checkEmail: false,
            verifyEmail: false,
        },

        error: false,
        errorType: null,

        // form options
        classStandings: profileJSON.classStandings,
        majorOptions: profileJSON.majors,
    };
};

export default {
    name: "LoginModal",

    components: {
        Modal,
        Multiselect,
    },

    props: {
        display: String,
        isOpen: Boolean,
    },

    mixins: [utilMixin],

    data() {
        return initialState(this.display);
    },

    computed: {
        // styling
        modalDimensions() {
            if (this.isMobile()) {
                return {
                    maxHeight: "100%",
                    minWidth: "100%",
                    minHeight: "100%",
                };
            } else {
                return {
                    maxWidth: "450px",
                    minWidth: "300px",
                    maxHeight: "fit-content",
                };
            }
        },
        modalCardStyles() {
            if (this.isMobile()) {
                return {
                    "border-radius": 0,
                    "background-color": "#fcf0ef",
                };
            } else {
                return {};
            }
        },
        modalHeaderStyles() {
            let padding = this.show["reset"] || this.show["checkEmail"] || this.show["verifyEmail"] ? 0 : "10px";
            return { "padding-bottom": padding };
        },
        modalTitleStyles() {
            return this.isMobile() ? "modal-card-title mobile" : "modal-card-title";
        },

		// populate data
        modalTitle() {
            if (this.show["reset"]) {
                return "Reset password";
            } else if (this.show["checkEmail"]) {
                return "Check your email";
            } else if (this.show["signUpOne"]) {
                return "Get on a ride with us";
            } else if (this.show["signUpTwo"]) {
                return "Just a bit more";
            } else if (this.show["verifyEmail"]) {
                return "Verify your email";
            } else {
                return "Welcome back";
            }
        },
        slotName() {
            if (this.show["reset"]) {
                return "reset";
            } else if (this.show["checkEmail"]) {
                return "checkEmail";
            } else if (this.show["signUpOne"]) {
                return "signUpOne";
            } else if (this.show["signUpTwo"]) {
                return "signUpTwo";
            } else if (this.show["verifyEmail"]) {
                return "verifyEmail";
            } else {
                return "logIn";
            }
        },

		// form completion
		signUpCompleted() {
			return  !this.nullOrEmpty(this.signUpData.name)  &&
                    !this.nullOrEmpty(this.signUpData.class) &&
                    !this.nullOrEmpty(this.signUpData.major);
		}
	},

    methods: {
        // Form validation and action methods
        emailValidation(email, uw = false) {
            email = email.trim();

            let reg;
            if (uw) {
                reg = /^([a-zA-Z0-9_.+-])+@uw.edu$/i;
            } else {
                reg = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
            }

            let validation = new RegExp(reg, "i");
            return validation.test(email);
        },
        inputFocus(event) {
            let target = event.target;
            target.style.backgroundColor = target == document.activeElement ? "#FDFDF9" : "transparent";
        },
        emailBlur(event) {
            let target = event.target;
            if (!this.nullOrEmpty(target.value)) {
                if (!this.emailValidation(target.value)) {
                    this.error = true;
                    this.errorType = "email";
                } else {
                    this.error = false;
                    this.errorType = null;
                }
            } else {
                target.style.backgroundColor = "transparent";
            }
        },
        blur(event) {
            let target = event.target;
            if (this.nullOrEmpty(target.value)) {
                target.style.backgroundColor = "transparent";
            }
        },
        onSelectClass(index) {
            let old = this.signUpData.class;
            this.signUpData.class = old == index ? null : index;
        },
        onSelectMajor() {
            let multiselect = this.$refs.multiselect.$el;
            let { search: input, tags } = this.$refs.multiselect.$refs;

            let isEmpty = this.nullOrEmpty(this.signUpData.major);
            multiselect.style["background"] = isEmpty ? "transparent" : "white";
            tags.style["margin-top"] = isEmpty ? 0 : "-5px";
            input.style["margin-top"] = isEmpty ? 0 : "10px";
        },

		// Navigation methods
        submitLogin() {
            // Validate that all fields have been filled out
            if (this.nullOrEmpty(this.email) || this.nullOrEmpty(this.password)) {
                return;
            }

			auth.signInWithEmailAndPassword(this.email, this.password)
			.then(() => {
				this.email = null;
				this.password = null;
				this.exitModal();

				// Force the parent navbar to update its state
				this.$emit('authUpdate', auth.currentUser);
			})
			.catch((error) => {

				// Handle Errors here.
				let errorCode = error.code;

				// TODO: Handle different error types
				if (errorCode == "auth/invalid-email") {
					this.errorType = "email";
				} else if (errorCode == "auth/user-disabled") {
					this.errorType = "contact-classy";
				} else if (errorCode == "auth/user-not-found") {
					this.errorType = "bad-login";
				} else if (errorCode == "auth/wrong-password") {
					this.errorType = "bad-login";
				}

				this.error = true;
			});

        },
        submitReset() {
            if (this.nullOrEmpty(this.email)) {
                return;
            }

            auth.sendPasswordResetEmail(this.email)
			.then(() => {
				this.email = null;
				this.goTo("reset", "checkEmail");
			})
			.catch((error) => {

				// Handle Errors here.
				let errorCode = error.code;

				// TODO: Handle different error types
				if (errorCode == "auth/invalid-email") {
					this.errorType = "email";
				} else if (errorCode == "auth/missing-continue-uri") {
					this.errorType = "contact-classy";
				} else if (errorCode == "auth/invalid-continue-uri") {
					this.errorType = "contact-classy";
				} else if (errorCode == "auth/user-not-found") {
					this.errorType = "contact-classy";
				}

				this.error = true;
			});

        },
		resendEmail() {
			if (this.nullOrEmpty(this.email)) {
                return;
            }

            auth.sendPasswordResetEmail(this.email)
			.then(() => {
				this.email = null;
				this.goTo("reset", "checkEmail");
			})
			.catch((error) => {

				// Handle Errors here.
				let errorCode = error.code;

				// TODO: Handle different error types
				if (errorCode == "auth/invalid-email") {
					this.errorType = "email";
				} else if (errorCode == "auth/missing-continue-uri") {
					this.errorType = "contact-classy";
				} else if (errorCode == "auth/invalid-continue-uri") {
					this.errorType = "contact-classy";
				} else if (errorCode == "auth/user-not-found") {
					this.errorType = "contact-classy";
				}

				this.error = true;
			});
		},
        submitEmail() {
            // TODO: password verification request
            /*
			auth().sendPasswordResetEmail('user@example.com', actionCodeSettings)
				.then(function() {
				  // Password reset email sent.
				})
				.catch(function(error) {
				  // Error occurred. Inspect error.code.
				});
			*/
        },
        signUpNextPage() {
            if (this.nullOrEmpty(this.email) || this.nullOrEmpty(this.password)) {
                return;
            } else if (this.emailValidation(this.email, true)) {
                this.goTo("signUpOne", "signUpTwo", false);
            } else {
                //  TODO: catch a SPECIFIC error
                this.error = true;
                this.errorType = "uw-email";
            }
        },
        submitSignUp() {
            if (this.signUpCompleted) {

				// Sign up with an email and password
                auth.createUserWithEmailAndPassword(this.email, this.password)
                    .then((userRecord) => {
                        // Update the user's profile name on Firebase
                        let name = this.signUpData.name;
                        userRecord.user.updateProfile({
                            displayName: name,
                            photoURL: "https://ui-avatars.com/api/?name=" + name,
                        });

                        // Send the email verification
                        auth.currentUser.sendEmailVerification();

                        // Let's upload some metadata for our user
                        firestore
                            .collection("university")
                            .doc("uw")
                            .collection("users")
                            .doc(userRecord.user.uid)
                            .set({
                                name: this.signUpData.name,
                                class: this.classStandings[this.signUpData.class],
                                major: this.signUpData.major,
                            })
                            .then(() => {
                                this.goTo("signUpTwo", "verifyEmail");
                            })
                            .catch((error) => {
                                // This is really bad! We created a sign up auth,
                                // but there is no stored data for the user
                                console.warn("Error getting document:", error);
                            });

                        // Force the parent navbar to update its state
                        this.$emit("authUpdate", auth.currentUser);

                        // Clear the fields
                        this.email = null;
                        this.password = null;
                    })
                    .catch((error) => {
                        // Handle Errors here.
                        let errorCode = error.code;
                        let errorMessage = error.message;

                        // TODO: Handle different error types
                        if (errorCode == "auth/email-already-in-use") {
                            alert(errorMessage);
                        } else if (errorCode == "auth/invalid-email") {
                            alert(errorMessage);
                        } else if (errorCode == "auth/weak-password") {
                            alert(errorMessage);
                        }

                        this.error = true;
                        // TODO: Set error type based on errorCode
                        this.errorType = null;
                    });
            }
        },

		// Helper methods
		goTo(prev, next, resetFields = true) {
            if (resetFields) {
                this.email = null;
                this.password = null;
            }

            this.error = false;
            this.errorType = null;

            this.show[prev] = false;
            this.show[next] = true;
        },
        exitModal() {
            Object.assign(this.$data, initialState(this.display));
            this.$emit("toggleModal");
        },
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import "@/styles/_main.scss";

// Error styles
.error-text {
    font-size: 14px;
    line-height: normal;
    margin: 0;
}

.error-banner {
    margin-bottom: 20px;
    padding: 16px 0;
    width: 100%;

    background: #f4e5de;
    border: 1px solid $error;
    border-radius: 8px;
    text-align: center;
}

input.error {
    border: 1px solid $error;
}

.error-label {
    margin-top: 8px;
}

// Login landing styles
.form {
    margin-top: -20px;
    width: 100%;
}

.form-label {
    margin: 0 0 6px 0;
}

.field {
    margin-top: 20px;

    input {
        color: #999999;
    }
}

input {
    padding: 16px 20px;
    width: calc(100% - 40px);

    background: transparent;
    border: 1px solid #999999;
    border-radius: 8px;

    font-size: 14px;
    line-height: 17px;
}

button.submit {
    margin-top: 24px;
    padding: 16px 0;
    width: 100%;

    background: #fe5775;
    border: transparent;
    border-radius: 16px;

    color: white;
    font-size: 14px;

    cursor: pointer;

    &:visited,
    &:focus,
    &:hover,
    &:disabled {
        outline: none;
        border: none;
    }

    &:disabled {
        cursor: default;
        background: lightgray;
    }
}

.card-footer {
    margin-top: 16px;
    width: 100%;

    p {
        margin-left: 24px;
        font-size: 14px;
        line-height: 14px;
        cursor: pointer;
    }

    :nth-child(1) {
        margin-left: 0px;
    }
}

@media only screen and (max-width: $breakpoint-medium) {
    .card-footer {
        p {
            margin-left: 0px;
        }

        :nth-child(1) {
            padding-right: 10px;
        }
    }
}

@media only screen and (max-width: $breakpoint-small) {
    .card-footer {
        display: inline-block;
        margin-top: 10px;

        p {
            line-height: 20px;
        }
    }
}

// Sign up styles
.label-select {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-top: -6px;
    margin-left: -12px;

    width: 100%;

    > * {
        margin-top: 15px;
        margin-left: 12px;
    }
}

.label-select-item {
    padding: 8px 12px;

    border: 1px solid #999999;
    border-radius: 8px;

    font-size: 14px;
    color: #999999;

    cursor: pointer;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &.active {
        background: $primary;
        border: 1px solid white;
        color: white;
    }
}

// Reset styles
p.subtitle {
    margin: 0 0 24px 0;
}

// Check styles
#verify-email {
    button.submit {
        margin-top: 16px;
    }

    .card-footer.soft-text {
        cursor: auto;
    }
}

.footer-link {
    text-decoration: underline;
    cursor: pointer;
}
</style>

<style lang="scss">
#login-modal-wrapper {
    .multiselect:focus {
        background: white;
    }

    .multiselect__select {
        display: none;
    }

    .multiselect--active {
        min-height: auto;
        background: white;
        border-radius: 8px;
    }

    .multiselect__content-wrapper {
        border: none;
        max-width: 100%;
        overflow-x: hidden;

        .multiselect__content {
            margin-block-start: 2em;
        }
    }

    .multiselect__placeholder,
    .multiselect__input,
    .multiselect__tags:not(.active),
    .multiselect__tag,
    .multiselect__option {
        font-family: Usual, Avenir, Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 17px;
    }

    .multiselect__option--highlight,
    .multiselect__option--highlight:after {
        background: #fe5775;
    }

    .multiselect__option {
        white-space: pre-wrap;
        max-width: 100%;
        font-size: 14px;
    }

    .multiselect__tags:not(.active) {
        padding: 16px 20px;
        min-height: auto;

        background: transparent;
        border: 1px solid #999999;
    }

    .multiselect__tag {
        color: white;
        background: #fe5775;
        margin-bottom: 0;
        margin-top: 5px;
    }

    .multiselect__tag-icon {
        &:hover {
            background: #fd133d;
        }
    }

    .multiselect__placeholder,
    .multiselect__input {
        margin-bottom: 0;
        padding: 0;

        background: transparent;
    }

    .multiselect__placeholder {
        color: #999999;
    }

    .multiselect__input {
        min-height: auto;
    }
}
</style>
